/* =========================================================================
   Daily Blessing — the lite daily-ritual loop. Playing one full game today lets
   Theo light the blessing candle. State is a single localStorage key holding the
   LOCAL day it was last lit (guest-safe; no server needed). lightToday() returns
   true only on the FIRST light of a new local day so the caller can celebrate +
   emit telemetry exactly once.

   House pattern: the logic is PURE over an injected storage seam (defaulting to
   localStorage) so tests inject a stub — mirrors dyk/easyStart's pure style.
   ========================================================================= */
const KEY = 'catholics.dailyLit';

/** Minimal storage seam (localStorage-compatible subset). */
export interface KVStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function deviceStore(): KVStore | null {
  try {
    // Even touching localStorage can throw in private/locked-down contexts.
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Local-day stamp `YYYY-MM-DD` (device timezone — the ritual is a personal day). */
export function localDateString(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isLitToday(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  try {
    return !!store && store.getItem(KEY) === localDateString(now);
  } catch {
    return false;
  }
}

/** Light today's candle. Returns true only when this is a NEW light for today. */
export function lightToday(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  const today = localDateString(now);
  try {
    if (!store) return false;
    if (store.getItem(KEY) === today) return false;
    store.setItem(KEY, today);
    return true;
  } catch {
    return false; // storage unavailable — candle simply won't persist
  }
}
